import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import { nanoid } from 'nanoid'
import { type CSSProperties, type ReactElement, type ReactNode } from 'react'
import urlRegex from 'url-regex-safe'
import { type ActionItem, type IButtonProps, type IIconButtonProps } from 'shared/ui'
import { calculatorGSM, formatBytesToNumber } from 'shared/lib'
import {
  type IParamsCreateMessage,
  type IParamsUpdateMessage,
  type MessageData,
} from 'entities/Message'
import { type ILimitSizeError, type IResponseAttachmentUpload } from 'entities/Attachment'
import { Attachment } from 'entities/Attachment/model/Attachment'
import { RequestAttachment } from 'entities/Attachment/model/RequestAttachment'
import { type Message } from 'entities/Message/model/Message'
import { type IResponseConversationDraftMessageData } from 'entities/Conversation/api/types'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import {
  allowedRcsExtensions,
  allowedRcsMimeTypes,
  openRcsNotSupportedFilesModal,
} from 'entities/Rcs'
import { textToHtml } from 'entities/Message/lib/textToHtml'
import { replaceMentionTagsWithSpans } from 'entities/Message/lib/parseNote'
import { DropdownMentionsStore } from 'widgets/MessageField/ui/DropdownMentions'
import { htmlToText } from 'widgets/MessageField/lib/htmlToText'
import { type IModalCloseProps } from 'widgets/Record'
import { RecordAudioStore } from 'widgets/RecordAudio'

import {
  EnumMessageFieldMode,
  EnumVariantMessageField,
  type IInputAction,
  SHORT_URL_POSTFIX_DEFAULT,
} from 'widgets/MessageField/store/types'
import type {
  IMessageFieldEvents,
  IMessageFieldHighlight,
  IMessageFieldLimit,
  IMessageFieldStoreProps,
} from 'widgets/MessageField'
import { airCallTooltip } from 'widgets/constants'
import { RecentStore } from 'widgets/RecentlySent'

export class MessageFieldStore {
  promiseAttachmentLoading: Promise<void> | null = null
  promiseAttachmentLoadingResolve: ((value: void | PromiseLike<void>) => void) | null = null

  actions: Array<ActionItem>
  showActionsItems: number
  private _noCounter?: boolean
  editId?: number | null = null

  private _messageHtml = ''
  private _messageInnerText = ''
  private _additionalText = ''
  private _messageFieldElement: HTMLDivElement | null = null
  private _textFieldElement: HTMLDivElement | null = null
  get textFieldElement() {
    return this._textFieldElement
  }
  setTextFieldElement = (element: HTMLDivElement | null) => {
    this._textFieldElement = element
  }

  focusMessageFieldTrigger = ''
  blurMessageFieldTrigger = ''
  activeMessageField = false
  private _disabled = false
  private _sendDisabled = false
  messageSignatureSendDisabled = false
  private _loading = false
  private _pendingUpload = 0
  isDoubleOptInEnabled = false
  replaceMergeFieldsOnPaste = false
  disableMergeFieldsFallback = false
  disableLinks = false
  private _isMarketing = false
  private _isRCS = false
  private _showRcsNotSupportedFilesAlert = false

  textLimit: IMessageFieldLimit = {
    maxLength: 1600,
    message: 'Message should not be longer than 1,600 characters',
    showAlert: true,
  }

  sendAlert: string | null = null

  private _attachmentsMap: Map<string | number, Attachment> = new Map()

  addContent: ((content: string, replaceContent?: string) => void) | null = null
  replaceContent: ((content: string, targetNode?: Node) => void) | null = null
  replaceSelectedContent: ((content: string) => void) | null = null
  limitSizeErrors: ILimitSizeError[] = []
  extError = ''
  isAirCallNoSupportMms = false
  moreDropdownComponent: ((onCloseMenu?: () => void) => ReactNode) | null = null
  onCloseMoreDropdown: (() => void) | null = null

  replaceTriggerContent = ''
  updateTriggerContent = ''
  private _selectedContent = ''
  convertFileIdsMap: Map<number, string> = new Map()
  private _placeholder: ReactNode = '⌘K for shortcuts...'
  styles: CSSProperties | null = null

  fromSavedReply = false
  resetOnUnmount = true

  onFocus: IMessageFieldEvents['onFocus'] | null = null
  onClick: IMessageFieldEvents['onClick'] | null = null
  onKeyDown: IMessageFieldEvents['onKeyDown'] | null = null
  onKeyUp: IMessageFieldEvents['onKeyUp'] | null = null
  onInit: IMessageFieldEvents['onInit'] | null = null
  onCloseEdit: IMessageFieldEvents['onCloseEdit'] | null = null

  alertElement: HTMLDivElement | null = null
  recordAudioState = false

  highlight: IMessageFieldHighlight | null = null
  inputActions = new Map<string, IInputAction>()
  sendActionBtnProps: IMessageFieldStoreProps['sendActionBtnProps'] = null
  editMessage: Message | null = null
  mode: EnumMessageFieldMode = EnumMessageFieldMode.Sms
  variant: EnumVariantMessageField = EnumVariantMessageField.Default
  private _mentionsStore: DropdownMentionsStore
  private _disposeParentMode: IReactionDisposer | null = null
  private _disposeUploadingFiles: IReactionDisposer | null = null
  private _disposeChangeIsAircall: IReactionDisposer | null = null
  private _isCurrentAirCall = false
  private _mergeFieldsCount = 0

  customSendAction?: ReactElement | null = null
  recordModalCloseProps?: IModalCloseProps | null = null
  withTrialText = false
  recordAudioStore = new RecordAudioStore()
  private readonly _recentStore: RecentStore
  private _isShortUrlPostfix = false
  private _sub_source: string | null = null

  constructor(props: IMessageFieldStoreProps, private _entities: IEntitiesStore) {
    this._recentStore = new RecentStore(this._entities)
    this._mentionsStore = new DropdownMentionsStore(this, this._entities)
    this.showActionsItems = props?.showActionsItems || 2
    this._noCounter = props?.noCounter || false
    this._placeholder = props?.placeholder || this._placeholder
    this.styles = props?.styles || null
    this.resetOnUnmount = props?.resetOnUnmount ?? true
    this.fromSavedReply = !!props?.fromSavedReply
    this.textLimit = Object.assign({}, this.textLimit, props?.textLimit)
    this.sendActionBtnProps = props?.sendActionBtnProps || null
    this.variant = props?.variant || EnumVariantMessageField.Default
    this.recordModalCloseProps = props?.recordModalCloseProps || null
    this.withTrialText = props?.withTrialText || false
    this.replaceMergeFieldsOnPaste = props?.replaceMergeFieldsOnPaste || false
    this.disableMergeFieldsFallback = props?.disableMergeFieldsFallback || false
    this.disableLinks = props?.disableLinks ?? false
    this._isShortUrlPostfix = props?.isShortUrlPostfix || false
    this._isCurrentAirCall = props?.isCurrentAirCall || false
    this._sub_source = props?.sub_source || ''
    this.highlight = props?.highlight ?? null
    this._isMarketing = props?.isMarketing ?? false
    this._isRCS = props?.isRCS ?? false

    if (props?.editMessage) {
      this.setEditMessage(props?.editMessage)
    }

    // Make events
    const events = props?.makeEvents?.(this)
    this.onFocus = events?.onFocus ?? null
    this.onClick = events?.onClick ?? null
    this.onKeyDown = events?.onKeyDown ?? null
    this.onKeyUp = events?.onKeyUp ?? null
    this.onInit = events?.onInit ?? null
    this.onCloseEdit = events?.onCloseEdit ?? null

    // Make actions
    this.actions = props?.makeActions?.(this) ?? []

    // Make input actions
    props?.makeInputActions?.(this)?.map(this.addInputAction)

    this.customSendAction = props?.customSendAction || null
    makeAutoObservable(this)

    this.reactionUploadingFiles()
    this.reactionChangeIsAircall()
  }

  get mentionsStore() {
    return this._mentionsStore
  }

  get placeholder() {
    return this._placeholder
  }

  get isMarketing() {
    return this._isMarketing
  }

  get isRCS() {
    return this._isRCS
  }

  private isRcsAudio(attachment: { content_type?: string }) {
    return (
      this.isRCS &&
      (attachment.content_type?.includes('audio/mpeg') ||
        attachment.content_type?.includes('audio/mp3'))
    )
  }

  get recentStore() {
    return this._recentStore
  }

  get showRcsNotSupportedFilesAlert() {
    return (
      this._showRcsNotSupportedFilesAlert && this.isRCS && this.mode !== EnumMessageFieldMode.Note
    )
  }

  get disabled() {
    return this._disabled
  }

  get messageHtml() {
    return this._messageHtml
  }

  get messageInnerText() {
    return this._messageInnerText
  }

  get messageFieldElement() {
    return this._messageFieldElement
  }

  get selectedContent() {
    return this._selectedContent
  }

  get isCurrentAirCall() {
    return this._isCurrentAirCall
  }

  get mergeFieldsCount() {
    return this._mergeFieldsCount
  }

  get attachments() {
    return Array.from(this._attachmentsMap.values())
  }

  get disabledFields() {
    if (this._isCurrentAirCall && !this._entities.featureFlagsStore.aircall_new_api) {
      return {
        uploadImage: airCallTooltip,
        recordAudio: '',
      }
    }

    return {}
  }

  get iconButtonProps(): IIconButtonProps {
    return {
      variant: this.isModeNote ? 'contained' : 'icon',
      color: this.isModeNote ? 'passiveSecondary' : 'secondary',
      ariaLabel: 'SendAction_send',
    }
  }

  get buttonProps(): IButtonProps {
    return {
      intent: this.isModeNote ? 'passive' : 'default',
      contained: this.isModeNote ? 'primary' : 'secondary',
    }
  }

  get isVariantNote() {
    return this.variant === EnumVariantMessageField.Note
  }

  get isVariantConversation() {
    return this.variant === EnumVariantMessageField.Conversation
  }

  get isVariantUnderline() {
    return this.variant === EnumVariantMessageField.Underline
  }

  get isVariantDefault() {
    return this.variant === EnumVariantMessageField.Default
  }

  get isVariantClean() {
    return this.variant === EnumVariantMessageField.Clean
  }

  get countShortUrl() {
    const text = this.textWithAttachmentsNotForMedia

    const supportedDomains = this._entities.shortLinkStore.allDomain?.supportedDomains || []
    const content = [...(text.match(urlRegex({ strict: false })) || [])].filter((url) =>
      supportedDomains.some((domain) => url.includes(domain))
    )

    return content.length
  }

  get fakeTextForConversion() {
    if (!this._isShortUrlPostfix) return ''

    return Array.from(Array(this.countShortUrl).keys()).reduce((state) => {
      state += SHORT_URL_POSTFIX_DEFAULT

      return state
    }, '')
  }

  get messageRemainsInfoRCSandMMS() {
    return {
      messageCount: Math.ceil(this.textLength / 1600) || (this.attachments.length && 1) || 0,
      messageRemains: this.textLength === 1600 ? 0 : 1600 - (this.textLength % 1600),
    }
  }

  private get _messageInfo() {
    return calculatorGSM(this.textWithAttachmentsNotForMedia + this.fakeTextForConversion)
  }

  get messageRemainsInfo() {
    if (this.isMMS || this.isRCS) return this.messageRemainsInfoRCSandMMS

    return {
      messageCount: this._messageInfo.sms_count,
      messageRemains: this._messageInfo.chars_left,
    }
  }

  get isUnicodeMessage() {
    if (this.isMMS || this.isRCS || this.isModeNote || !this.messageInnerText.trim()) {
      return false
    }

    return this._messageInfo.char_set === 'Unicode'
  }

  get textLimitReached() {
    return this.textLimit.maxLength - this.textLength < 0
  }

  get attachmentsForMms() {
    return this.attachments.filter((attachment) => {
      const isRcsAudio = this.isRcsAudio(attachment)
      return (attachment.is_allowed_for_media_url && !isRcsAudio) || attachment.media_preview
    })
  }

  get uploadingFiles() {
    return this.attachments.filter((attachment) => attachment.loading)
  }

  get isAttachmentsCountLimitError() {
    return this.attachmentsForMms.length > 5
  }

  get messageRequestData(): IParamsCreateMessage {
    return {
      client_id: '',
      manual_created: true,
      media_url: this.media_url,
      message: this.messageTextForBackAnd,
      type: this.isModeNote ? 'note' : null,
      sub_source: this._sub_source || null,
    }
  }

  get messageDraftData(): IResponseConversationDraftMessageData {
    return {
      audio_url: [],
      docs_url: [],
      images_url: [],
      video_url: [],
      media_url: this.media_url.map((item) => item.attachmentDraft),
      manual_created: true,
      message: this.messageText,
      send_at: this.editMessage?.send_at || '',
      stop_on_response: this.editMessage?.stop_on_response || false,
    }
  }

  get isEmptyDraft() {
    return Boolean(!this.messageDraftData.media_url.length && !this.messageDraftData.message.length)
  }

  get paramsUpdateMessage(): IParamsUpdateMessage {
    return {
      manual_created: true,
      media_url: this.media_url,
      body: this.messageTextForBackAnd,
      stop_on_response: this.editMessage?.stop_on_response || false,
      send_at: this.editMessage?.send_at || '',
      is_contact_timezone: this.editMessage?.is_contact_timezone || false,
    }
  }

  get loadingSend() {
    return this._loading
  }

  get isSmallAirCallAttach() {
    return this.attachmentsForMms.some((attach) => {
      if (attach.type === 'video') {
        return formatBytesToNumber(attach.size || 0) < 600
      }

      if (attach.type === 'audio') {
        return formatBytesToNumber(attach.size || 0) < 5000
      }

      return attach.type === 'image' || attach.type === 'gif' || attach.type === 'vcard'
    })
  }

  get isMMSWithAirCallCheck() {
    if (this.isCurrentAirCall && !this._entities.featureFlagsStore.aircall_new_api) {
      return this.isSmallAirCallAttach
    }

    return this.isMMS
  }

  get disablesSend() {
    if (
      this._sendDisabled ||
      this.textLimitReached ||
      this.isAttachmentsCountLimitError ||
      this.messageSignatureSendDisabled ||
      this.loadingAttachment
    ) {
      return true
    }

    if (
      this.isCurrentAirCall &&
      !this._entities.featureFlagsStore.aircall_new_api &&
      this.isSmallAirCallAttach
    ) {
      return true
    }

    return !this.hasText && !this.attachments.length
  }

  get hasText() {
    return !!this.text.length
  }

  hasMention() {
    return this.messageHtml.includes('{tag:user:') || this.messageHtml.includes('{tag:inbox}')
  }

  get text() {
    return htmlToText(this._messageHtml).trim()
  }

  get messageText() {
    return this.text
  }

  get additionalTextCondition() {
    return this.editMessage ? '' : this._additionalText
  }

  get messageTextForBackAnd() {
    return `${this.messageText}${this.additionalTextCondition}`
  }

  get isMMS() {
    return this.attachments.some((attachment) => {
      const isRcsAudio = this.isRcsAudio(attachment)
      return (
        (attachment.is_allowed_for_media_url && !isRcsAudio) ||
        attachment.media_preview?.is_allowed_for_media_url
      )
    })
  }

  get media_url() {
    const media_url: RequestAttachment[] = []
    this.attachments.forEach((attachment) => {
      const isRcsAudio = this.isRcsAudio(attachment)
      if (attachment.is_allowed_for_media_url && !isRcsAudio) {
        media_url.push(new RequestAttachment(attachment))
      }

      if (attachment.media_preview) {
        attachment.media_preview.original_source = attachment.source
        attachment.media_preview.source_short = attachment.source_short
        const prevMedia = new RequestAttachment(attachment.media_preview)
        media_url.push(prevMedia)
      }
    })

    return media_url
  }

  get textWithAttachmentsNotForMedia() {
    const trialText =
      this.withTrialText && this._entities.subscriptionStore.isTrial ? 'Salesmsg Trial: ' : ''

    const text = trialText + this.messageInnerText.trim()

    if (this.additionalTextCondition) {
      return this.messageTextForBackAnd
    }

    return text
  }

  get textLength() {
    return this.textWithAttachmentsNotForMedia.length + this.fakeTextForConversion.length
  }

  get loadingAttachment() {
    return this._pendingUpload > 0 || this.attachments.some((item) => item.loading)
  }

  get conversationContact() {
    const contactId = this._entities.conversationStore.currentItem?.contact_id
    if (!contactId) return null

    const contact = this._entities.contactsStore.getItem(contactId)
    if (!contact) return null

    return contact
  }

  get isModeSms() {
    return this.mode === EnumMessageFieldMode.Sms
  }

  get isModeNote() {
    return this.mode === EnumMessageFieldMode.Note
  }

  get noCounter() {
    return this.isModeNote || this._noCounter
  }

  get isShowCustomShortUrlBanner() {
    return this._entities.shortLinkStore.isShowCustomShortUrlBanner
  }

  get isValidateRcsAttachments() {
    return this.isRCS && this.mode !== EnumMessageFieldMode.Note
  }

  get hasInvalidRcsAttachments() {
    return this.attachments.some((attachment) => {
      const fileType = attachment.content_type
      const fileExtension = attachment.source?.split('.').pop()?.toLowerCase()
      return (
        !allowedRcsMimeTypes.includes(fileType) &&
        (!fileExtension || !allowedRcsExtensions.includes(fileExtension))
      )
    })
  }

  setShowRcsNotSupportedFilesAlert = (value: boolean) => {
    this._showRcsNotSupportedFilesAlert = value
  }

  setIsRCS = (value: boolean) => {
    this._isRCS = value
  }

  setSubSource = (value: string) => {
    this._sub_source = value
  }

  setIsCurrentAirCall = (value: boolean) => {
    this._isCurrentAirCall = value
  }

  handleResetMode = () => {
    this.mode = EnumMessageFieldMode.Sms
    this.setPlaceholder('Write a message...')
  }

  handleToggleMode = () => {
    if (this.isRCS && this.mode === EnumMessageFieldMode.Note && this.hasInvalidRcsAttachments) {
      openRcsNotSupportedFilesModal({
        onContinue: () => {
          this.mode = EnumMessageFieldMode.Sms
          this.setPlaceholder('Write a message...')
        },
      })

      return
    }

    this.mode =
      this.mode === EnumMessageFieldMode.Sms ? EnumMessageFieldMode.Note : EnumMessageFieldMode.Sms

    this.setPlaceholder(this.isModeNote ? 'Type @ to mention...' : 'Write a message...')
  }

  setNoteMode = (isChangePlaceholder?: boolean) => {
    this.mode = EnumMessageFieldMode.Note

    if (isChangePlaceholder) {
      this.setPlaceholder('Type @ to mention...')
    }
  }

  setAlertElement = (alertElement: HTMLDivElement) => {
    this.alertElement = alertElement
  }

  setMergeFieldsCount = (count: number) => {
    this._mergeFieldsCount = count
  }

  setMessageFieldElement = (messageFieldElement: HTMLDivElement) => {
    this._messageFieldElement = messageFieldElement
  }

  setActions = (actions: typeof this.actions) => {
    this.actions = actions
  }

  addInputAction = (action: IInputAction) => {
    this.inputActions.set(action.value, action)
  }

  setPlaceholder = (value: string) => {
    this._placeholder = value
  }

  setEditId = (id: number | null) => {
    this.editId = id
  }

  clear = () => {
    this._messageInnerText = ''
    this._messageHtml = ''
    this._disabled = false
    this.editId = null
  }

  clearWindowSelection = () => {
    window.getSelection()?.empty()
  }

  resetReactions = () => {
    this._disposeParentMode?.()
    this._disposeUploadingFiles?.()
  }

  reset = (resetActions?: boolean, noResetEditMessage?: boolean) => {
    this._loading = false
    this.onCloseMoreDropdown?.()

    if (resetActions) {
      this.actions = []
    }

    this.recordAudioState = false
    this.replaceTriggerContent = ''
    this.focusMessageFieldTrigger = ''
    this.blurMessageFieldTrigger = ''

    this._attachmentsMap.clear()
    this._messageInnerText = ''
    this._messageHtml = ''

    this._disabled = false

    this.limitSizeErrors = []
    this.clearExtError()
    this.editId = null
    if (!noResetEditMessage) {
      this.editMessage = null
    }
    this.moreDropdownComponent = null
    this.mentionsStore.dispose()
    this.mentionsStore.disposeReactions()
  }

  setMessageText = (html: string, innerText: string) => {
    this._messageHtml = html
    this._messageInnerText = innerText
  }

  setFocusMessageFieldTrigger = () => {
    if (this.disabled) return

    this.focusMessageFieldTrigger = nanoid()
  }

  setBlurMessageFieldTrigger = () => {
    this.blurMessageFieldTrigger = nanoid()
  }

  setDisabledMessageField = (value: boolean) => {
    if (value && this.activeMessageField) {
      this.setBlurMessageFieldTrigger()
    }

    this._disabled = value
  }

  setSendDisabled = (value: boolean) => {
    this._sendDisabled = value
  }

  setMessageSignatureSendDisabled = (value: boolean) => {
    this.messageSignatureSendDisabled = value
  }

  setAddContentAction = (action: typeof this.addContent) => {
    this.addContent = action
  }

  setReplaceSelectionAction = (action: typeof this.addContent) => {
    this.replaceSelectedContent = action
  }

  setReplaceContentFn = (fn: ((content: string, targetNode?: Node) => void) | null) => {
    this.replaceContent = fn
  }

  addAttachments(attachments: Attachment[]) {
    const newAttachments: Attachment[] = []

    attachments.forEach((attachment) => {
      if (this.attachments.some((item) => item.id === attachment.id)) return

      this._entities.attachmentStore.setPreviousAttachments([attachment])
      this._attachmentsMap.set(attachment.id, attachment)
      newAttachments.push(attachment)
    })

    if (newAttachments.length > 0) {
      this.addPreviewTextForAttachments(newAttachments)
    }
  }

  private addPreviewTextForAttachments(attachments: Attachment[]) {
    const attachmentsNeedingText = attachments.filter((attachment) => {
      // Add text for attachments that are not allowed for media_url (documents, large videos, audio)
      const isRcsAudio = this.isRcsAudio(attachment)
      if (attachment.is_allowed_for_media_url && !isRcsAudio) {
        return false
      }

      // Don't add preview text for blob URLs - wait for real URLs from server
      if (attachment.source?.startsWith('blob:')) {
        return false
      }

      // Check if preview text for this attachment already exists in the message
      const shortUrl = attachment.source_short || attachment.source
      const currentText = this.messageInnerText
      const urlAlreadyInText = shortUrl && currentText.includes(shortUrl)

      return !urlAlreadyInText
    })

    if (attachmentsNeedingText.length === 0) return

    const previewTexts = attachmentsNeedingText
      .map((attachment) => {
        const shortUrl = attachment.source_short || attachment.source

        let previewText: string
        if (attachment.media_preview) {
          previewText = `Here is a video preview. To watch the whole video click on the link: ${shortUrl}`
        } else {
          previewText = `${shortUrl}`
        }

        return previewText
      })
      .filter(Boolean)

    if (previewTexts.length > 0) {
      previewTexts.forEach((previewText) => {
        const previewHtml = textToHtml(this._entities, {
          text: previewText,
          replaceNewRow: true,
          ignoreLinks: this.disableLinks,
          extraSpace: true,
        })

        if (this.addContent) {
          this.addContent('<br>' + previewHtml)
        }
      })

      this.replaceTriggerContent = nanoid()
    }
  }

  setLimitSizeError = (limitError: ILimitSizeError) => {
    if (this.limitSizeErrors.some((item) => item.label === limitError.label)) return

    this.limitSizeErrors.push(limitError)
    setTimeout(() => {
      this.limitSizeErrors = []
    }, 5000)
  }

  deleteLimitSizeError = (label: string) => {
    this.limitSizeErrors = this.limitSizeErrors.filter((item) => item.label !== label)
  }

  setExtError = (extError: string) => {
    this.extError = extError

    setTimeout(() => {
      this.clearExtError()
    }, 5000)
  }

  clearExtError = () => {
    this.extError = ''
  }

  setSendAlert(alert: string) {
    this.sendAlert = alert
  }

  clearSendAlert() {
    this.sendAlert = null
  }

  setConvertAttachment = (data?: IResponseAttachmentUpload) => {
    if (data?.id) {
      const convertId = this.convertFileIdsMap.get(data?.id)
      if (convertId) {
        const attachment = new Attachment({ responseUpload: data })
        this.convertFileIdsMap.delete(data.id)
        this.addAttachment(attachment)
      }
    }
  }

  validateRcsAttachments() {
    if (!this.attachments.length || !this.isValidateRcsAttachments) return

    const attachments = this.attachments
    const skippedAttachmentsIds: (string | number)[] = []

    for (const attachment of attachments) {
      const fileType = attachment.content_type
      const fileExtension = attachment.source?.split('.').pop()?.toLowerCase()

      if (
        !allowedRcsMimeTypes.includes(fileType) &&
        (!fileExtension || !allowedRcsExtensions.includes(fileExtension))
      ) {
        skippedAttachmentsIds.push(attachment.id)
      }
    }

    if (skippedAttachmentsIds.length) this.removeAttachments(skippedAttachmentsIds)
  }

  async addMediaFiles(files: File[]) {
    let filesToUpload = files

    if (this.isValidateRcsAttachments) {
      filesToUpload = files.filter((file) => {
        const fileType = file.type
        const fileExtension = file.name.split('.').pop()?.toLowerCase()

        return (
          allowedRcsMimeTypes.includes(fileType) ||
          (fileExtension && allowedRcsExtensions.includes(fileExtension))
        )
      })

      const skippedCount = files.length - filesToUpload.length
      if (skippedCount > 0) this.setShowRcsNotSupportedFilesAlert(true)
      if (filesToUpload.length === 0) return
    }

    this._pendingUpload += 1
    try {
      await this._entities.attachmentStore.addMediaFiles({
        files: filesToUpload,
        addAttachment: this.addAttachment,
        setExtError: this.setExtError,
        setLimitSizeError: this.setLimitSizeError,
      })
    } finally {
      runInAction(() => {
        this._pendingUpload -= 1
      })
    }
  }

  addAttachment = (attachment: Attachment, update = false) => {
    if (this.isValidateRcsAttachments) {
      const fileType = attachment.content_type
      const fileExtension = attachment.source?.split('.').pop()?.toLowerCase()

      if (
        !allowedRcsMimeTypes.includes(fileType) &&
        (!fileExtension || !allowedRcsExtensions.includes(fileExtension))
      ) {
        this.setShowRcsNotSupportedFilesAlert(true)
        return
      }
    }

    if (update) {
      const exist = this._attachmentsMap.get(attachment.id)

      if (exist) {
        const wasAllowedForMediaUrl = exist.is_allowed_for_media_url
        const oldSource = exist.source
        this._attachmentsMap.set(attachment.id, attachment)

        const isRcsAudio = this.isRcsAudio(attachment)
        if (
          (wasAllowedForMediaUrl && !attachment.is_allowed_for_media_url) ||
          (!attachment.is_allowed_for_media_url && oldSource !== attachment.source) ||
          (isRcsAudio && oldSource !== attachment.source)
        ) {
          this.addPreviewTextForAttachments([attachment])
        }
      }
    } else {
      this._attachmentsMap.set(attachment.id, attachment)
      this.addPreviewTextForAttachments([attachment])
    }
  }

  removeAttachment = (id: number | string) => {
    this._attachmentsMap.delete(id)
  }

  removeAttachments = (ids: Array<string | number>) => {
    const attachmentsToRemove = ids
      .map((id) => this._attachmentsMap.get(id))
      .filter((attachment): attachment is Attachment => Boolean(attachment))

    ids.forEach((id) => {
      this.removeAttachment(id)
    })

    this.cleanupAttachmentLinksFromMessage(attachmentsToRemove)
  }

  private cleanupAttachmentLinksFromMessage = (removedAttachments: Attachment[]) => {
    if (!removedAttachments.length) return

    const originalHtml = this.messageHtml
    let currentHtml = originalHtml

    removedAttachments.forEach((attachment) => {
      if (attachment.source_short) {
        const attachmentShortUrl = attachment.source_short
        const escapedUrl = attachmentShortUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

        // Check if this attachment is a GIF preview for a large video
        // GIF preview has: content_type === 'image/gif' && original_source !== source
        const isGifPreview = !!(
          attachment.content_type === 'image/gif' &&
          attachment.original_source &&
          attachment.source &&
          attachment.original_source !== attachment.source
        )

        // Also check if this attachment has media_preview (large video with GIF)
        const hasMediaPreview = !!attachment.media_preview

        const urlPatterns = [
          // Remove complete video preview patterns with <a> tags
          new RegExp(
            `Here is a video preview\\. To watch the whole video click on the link: <a[^>]*href="${escapedUrl}"[^>]*>[^<]*</a>\\s*(<br\\s*/?\\s*>)?`,
            'gi'
          ),
          new RegExp(
            `Here is a video preview[^<]*<a[^>]*href="${escapedUrl}"[^>]*>[^<]*</a>\\s*(<br\\s*/?\\s*>)?`,
            'gi'
          ),
          // Remove standalone <a> tags with the URL
          new RegExp(`<a[^>]*href="${escapedUrl}"[^>]*>[^<]*</a>\\s*(<br\\s*/?\\s*>)?`, 'gi'),
          new RegExp(`<a[^>]*>${escapedUrl}</a>\\s*(<br\\s*/?\\s*>)?`, 'gi'),
          // Remove plain text URLs (fallback)
          new RegExp(escapedUrl + '\\s*(<br\\s*/?\\s*>)?', 'g'),
        ]

        urlPatterns.forEach((pattern) => {
          currentHtml = currentHtml.replace(pattern, '')
        })

        // Only remove orphaned video preview text if this is a GIF preview or has media_preview
        // This prevents removing video preview text when deleting other attachments (images, documents, etc.)
        if (isGifPreview || hasMediaPreview) {
          const orphanedTextPatterns = [
            new RegExp(
              'Here is a video preview\\. To watch the whole video click on the link:\\s*<\\/span>',
              'gi'
            ),
            new RegExp('Here is a video preview[^<]*<\\/span>', 'gi'),
          ]

          orphanedTextPatterns.forEach((pattern) => {
            currentHtml = currentHtml.replace(pattern, '')
          })
        }

        currentHtml = currentHtml
          .replace(/<span[^>]*>\s*<\/span>/gi, '')
          .replace(/<br>\s*<br>/gi, '<br>')
          .replace(/\s+/g, ' ')
          .trim()
      }
    })

    const htmlChanged = currentHtml !== originalHtml

    if (htmlChanged) {
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = currentHtml
      const newInnerText = tempDiv.innerText || tempDiv.textContent || ''

      this.setMessageText(currentHtml, newInnerText)
      this.replaceTriggerContent = nanoid()
    }
  }

  setMoreDropdownComponent = (moreDropdownComponent: typeof this.moreDropdownComponent) => {
    this.moreDropdownComponent = moreDropdownComponent
  }

  setOnCloseMoreDropdown = (onCloseMoreDropdown: typeof this.onCloseMoreDropdown) => {
    this.onCloseMoreDropdown = onCloseMoreDropdown
  }

  setHighlight = (highlight: IMessageFieldHighlight) => {
    this.highlight = highlight
  }

  clearHighlight = () => {
    this.highlight = null
  }

  setMessageData = ({
    message,
    attachments,
    noMergeField,
    isReset = true,
    isFocus = true,
    isReplace = false,
    replaceNewRow = true,
    replaceActionChips,
  }: MessageData) => {
    if (isReset) {
      this.reset(false, true)
    }

    let newMessageHtml = textToHtml(this._entities, {
      text: message,
      replaceNewRow: replaceNewRow,
      noMergeField,
      ignoreLinks: this.disableLinks,
      extraSpace: true,
      replaceActionChips,
    })

    if (newMessageHtml.includes('{tag:')) {
      newMessageHtml = replaceMentionTagsWithSpans(
        newMessageHtml,
        this._entities.usersStore.usersMap
      )
    }

    if (isReplace) {
      this._attachmentsMap.clear()
      this.addAttachments(attachments)

      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = newMessageHtml
      const extractedInnerText = tempDiv.innerText || tempDiv.textContent || message
      this.setMessageText(newMessageHtml, extractedInnerText)
    } else {
      this.addAttachments(attachments)
      this.addContent?.(newMessageHtml + '&nbsp;')
    }

    this.replaceTriggerContent = nanoid()
    if (isFocus) {
      this.focusMessageFieldTrigger = nanoid()
    }
  }

  replaceMessageText = (message: string) => {
    this.setMessageData({
      message,
      attachments: [],
      isReplace: true,
    })
  }

  setEditMessage = (message: Message | null) => {
    this.setMessageData({
      message: message?.body || '',
      attachments: message?.media || [],
      isReplace: true,
    })
    this.editMessage = message
  }

  setUpdateTriggerContent = () => {
    this.updateTriggerContent = nanoid()
  }

  replaceActionChip = (chipElement: HTMLSpanElement, newChipHtml: string | null) => {
    const textFieldEl = this._textFieldElement
    if (!textFieldEl) return

    if (!newChipHtml) {
      chipElement.parentElement?.remove()
    } else {
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = newChipHtml
      const newChipEl = tempDiv.firstChild
      if (newChipEl && chipElement.parentElement) {
        chipElement.parentElement.replaceChild(newChipEl, chipElement)
      }
    }

    this.setMessageText(textFieldEl.innerHTML, textFieldEl.innerText)
    this.setUpdateTriggerContent()
  }

  setRecordAudioState = (value: boolean) => {
    this.recordAudioState = value
  }

  setSendActionBtnProps = (sendActionBtnProps: typeof this.sendActionBtnProps) => {
    this.sendActionBtnProps = sendActionBtnProps
  }

  setSelectedContent = (value: string) => {
    this._selectedContent = value
  }

  reactionParentMode = () => {
    this._disposeParentMode?.()
    this._disposeParentMode = reaction(
      () => this.mode,
      () => {
        this.setFocusMessageFieldTrigger()
        this.validateRcsAttachments()
      }
    )
  }

  reactionUploadingFiles = () => {
    this._disposeUploadingFiles?.()
    this._disposeUploadingFiles = reaction(
      () => this.uploadingFiles.length,
      (size) => {
        if (size) {
          this.promiseAttachmentLoading = new Promise((resolve) => {
            this.promiseAttachmentLoadingResolve = resolve
          })
        } else {
          if (this.promiseAttachmentLoadingResolve) {
            this.promiseAttachmentLoadingResolve()
          }
        }
      }
    )
  }

  reactionChangeIsAircall = () => {
    this._disposeChangeIsAircall?.()
    this._disposeChangeIsAircall = reaction(
      () => [this._isCurrentAirCall, this.isSmallAirCallAttach],
      ([isCurrentAirCall, isSmallAirCallAttach]) => {
        if (isCurrentAirCall) {
          this.isAirCallNoSupportMms = isSmallAirCallAttach
        } else {
          this.isAirCallNoSupportMms = false
        }
      }
    )
  }

  handleCloseCustomShortUrlBanner = () => {
    this._entities.shortLinkStore.handleCloseCustomShortUrlBanner()
  }

  setLoading = (loading: boolean) => {
    this._loading = loading
  }

  setIsDoubleOptInEnabled = (isDoubleOptInEnabled: boolean) => {
    this.isDoubleOptInEnabled = isDoubleOptInEnabled
  }

  setActiveMessageField = (value: boolean) => {
    this.activeMessageField = value
  }

  setAdditionalText = (text: string) => {
    this._additionalText = text
  }
}
